import { sendOtp, otpLogin } from './identity';

const STORAGE_KEY = 'wassiniUser';
const PENDING_KEY = 'pendingLogin';
const RETURN_KEY = 'loginReturnPath';
const TOKEN_KEY = 'wassiniToken';
const META_KEY = 'myUser';
const GUEST_KEY = 'wassiniGuest';

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const getCurrentUser = () => {
  if (typeof window === 'undefined') return null;
  return safeJsonParse(window.localStorage.getItem(STORAGE_KEY));
};

export const setCurrentUser = (payload) => {
  if (typeof window === 'undefined') return;
  if (payload) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.localStorage.removeItem(GUEST_KEY);
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
};

export const isLoggedIn = () => Boolean(getCurrentUser());

// Asks identity to send a code by WhatsApp and remembers which phone is logging in
export const requestOtp = async (phone, profile = {}) => {
  if (typeof window === 'undefined') return { success: false };
  const sanitized = String(phone || '').replace(/\s+/g, '');
  if (!sanitized) {
    return { success: false, message: 'Phone number is required.' };
  }
  try {
    const response = await sendOtp(sanitized);
    if (response?.status === false) {
      return { success: false, message: response?.message };
    }
  } catch (error) {
    return { success: false, message: error.message };
  }
  window.sessionStorage.setItem(
    PENDING_KEY,
    JSON.stringify({
      phone: sanitized,
      createdAt: Date.now(),
      profile,
    })
  );
  return { success: true };
};

export const getPendingLogin = () => {
  if (typeof window === 'undefined') return null;
  return safeJsonParse(window.sessionStorage.getItem(PENDING_KEY));
};

const clearPendingLogin = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_KEY);
};

const buildCurrentPath = () => {
  if (typeof window === 'undefined') return '/';
  return (
    window.location.pathname +
    window.location.search +
    window.location.hash
  );
};

const sanitizeReturnPath = (path) => {
  if (!path || typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    if (typeof window === 'undefined') return null;
    try {
      const url = new URL(trimmed, window.location.origin);
      return url.pathname + url.search + url.hash;
    } catch {
      return null;
    }
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

export const setLoginReturnPath = (path) => {
  if (typeof window === 'undefined') return;
  try {
    const targetPath =
      sanitizeReturnPath(path) ||
      buildCurrentPath() ||
      '/';
    window.sessionStorage.setItem(RETURN_KEY, targetPath);
  } catch {
    // ignore storage errors
  }
};

export const getLoginReturnPath = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(RETURN_KEY);
  } catch {
    return null;
  }
};

export const consumeLoginReturnPath = () => {
  if (typeof window === 'undefined') return null;
  try {
    const target = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    return target;
  } catch {
    return null;
  }
};

export const redirectToLogin = (nextPath = null) => {
  if (typeof window === 'undefined') return;
  setLoginReturnPath(nextPath || buildCurrentPath());
  window.location.assign('/login');
};

// Checks the code with identity; on success returns the logged-in user and its token
export const verifyPendingOtp = async (code) => {
  const pending = getPendingLogin();
  if (!pending) {
    return { success: false, message: 'No OTP request found.' };
  }
  const submitted = String(code || '').replace(/\s+/g, '');

  let auth;
  try {
    auth = await otpLogin(pending.phone, submitted);
  } catch {
    return { success: false, message: 'Incorrect code. Please try again.' };
  }

  const profile = pending.profile || {};
  const remote = auth.user || {};
  const user = {
    id: remote.user_id || profile.id,
    phone: remote.phoneNumber || pending.phone,
    name: remote.firstName || profile.name || 'Wassini Member',
    email: remote.email || profile.email || '',
  };
  clearPendingLogin();
  return { success: true, user, token: auth.access_token, role: auth.role, profile };
};

export const persistUserSession = ({ user, token, role = 'customer' }) => {
  if (!user) return;
  setCurrentUser(user);
  if (typeof window === 'undefined') return;
  if (token) {
    window.localStorage.setItem(TOKEN_KEY, token);
  }
  window.localStorage.setItem(
    META_KEY,
    JSON.stringify({
      customer_id: user.id,
      role,
    })
  );
};

export const logout = () => {
  setCurrentUser(null);
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(META_KEY);
    window.localStorage.removeItem(GUEST_KEY);
  }
  clearPendingLogin();
};

export const enableGuestMode = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(GUEST_KEY, '1');
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(META_KEY);
};

export const disableGuestMode = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(GUEST_KEY);
};

export const isGuestSession = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(GUEST_KEY) === '1';
};
